import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const categoryLabels: Record<string, string> = {
  "market-insights": "Market Insights",
  "brand-spotlight": "Brand Spotlight",
};

const categoryIcons: Record<string, string> = {
  news: "ri-newspaper-line",
  logistics: "ri-truck-line",
  "market-insights": "ri-bar-chart-line",
  company: "ri-building-line",
  "brand-spotlight": "ri-star-line",
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  category: string | null;
  image_url: string | null;
  published_at: string | null;
}

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaErr } = await supabase
        .from("posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (supaErr) throw supaErr;
      if (!data) {
        setError("Post not found.");
      } else {
        setPost(data as Post);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load post");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  // SEO
  usePageSEO({
    title: post ? `${post.title} | GH Corporation News` : "News | GH Corporation",
  });

  const articleBreadcrumb = post ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
      { "@type": "ListItem", "position": 2, "name": "News", "item": siteUrl + "/news" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": siteUrl + "/news/" + post.slug }
    ]
  } : null;

  const articleSchema = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt || "",
    "datePublished": post.published_at || "",
    "publisher": {
      "@type": "Organization",
      "name": "GH Corporation",
      "url": siteUrl
    }
  } : null;

  if (loading) {
    return (
      <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 bg-background-100 min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-accent-200 border-t-accent-500 rounded-full animate-spin" />
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 bg-background-100 min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-foreground-600 text-[15px]">{error || "Post not found."}</p>
        <Link
          to="/news"
          className="mt-6 whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[13px] tracking-[0.12em] uppercase"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
          Back to News
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full bg-background-100">
      {articleBreadcrumb && <JsonLd data={articleBreadcrumb} />}
      {articleSchema && <JsonLd data={articleSchema} />}
      {/* Hero / Header */}
      <div className="relative px-6 md:px-12 lg:px-20 pt-28 pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-[13px] font-label tracking-[0.06em] text-foreground-500 hover:text-accent-700 transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            All News
          </Link>

          {/* Category & Date */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-[12px] font-label tracking-[0.06em] uppercase border border-secondary-200">
              <i className={`${categoryIcons[post.category || "news"] || "ri-newspaper-line"} w-3.5 h-3.5 flex items-center justify-center`} />
              {categoryLabels[post.category || ""] || post.category || "News"}
            </span>
            <span className="text-[13px] font-label tracking-[0.06em] text-foreground-400">
              {formatDate(post.published_at || "")}
            </span>
          </div>

          <h1 className="font-heading text-foreground-950 text-3xl md:text-4xl lg:text-5xl leading-[1.1] font-light">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-5 text-foreground-700 text-[16px] md:text-lg leading-relaxed font-light max-w-3xl">
              {post.excerpt}
            </p>
          )}
        </div>
      </div>

      {/* Optional Featured Image */}
      {post.image_url && (
        <div className="px-6 md:px-12 lg:px-20 pb-10">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden h-64 md:h-80 lg:h-96">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Article Content */}
      <div className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="max-w-4xl mx-auto">
          <article className="prose prose-lg max-w-none">
            {post.content?.split("\n\n").map((paragraph, idx) => (
              <p key={idx} className="text-foreground-800 text-[15px] md:text-base leading-[1.8] mb-6">
                {paragraph}
              </p>
            ))}
          </article>

          {/* Bottom CTA */}
          <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <p className="text-foreground-600 text-[15px]">
              Have questions about this announcement?
            </p>
            <Link
              to="/contact"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[12px] tracking-[0.14em] uppercase"
            >
              Contact Us
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>

          <div className="mt-8">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-[13px] font-label tracking-[0.06em] text-foreground-500 hover:text-accent-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Back to all news
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}